<template>
    <div>
        <b-overlay :show="busy" :z-index="1051">
            <worksheet-general-information ref="worksheetGeneralInformation"
                                           :api-base-url="apiBaseUrl"
                                           :images-url="imagesUrl"
                                           :file-download-url="fileDownloadUrl"
                                           :current-user-account-id="currentUserAccountId"
                                           :work-item-id="workItemId"
                                           :work-item="workItem"
                                           :related-court-sitting-id="relatedCourtSittingId"
                                           :related-court-matter-id="relatedCourtMatterId"
                                           :related-interview-id="relatedInterviewId"
                                           :related-list-court-id="relatedListCourtId"
                                           :assignment-relevant-roles="assignmentRelevantRoles"
                                           :assignment-roles-with-audio-minutes="assignmentRolesWithAudioMinutes"
                                           :assignment-roles-with-round-robin-priority="assignmentRolesWithRoundRobinPriority"
                                           :all-audio-sub-status-options="allAudioSubStatusOptions"
                                           :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                                           :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                                           :all-deposit-sub-status-options="allDepositSubStatusOptions"
                                           :all-vetting-sub-status-options="allVettingSubStatusOptions"
                                           :all-audio-quality-options="allAudioQualityOptions"
                                           :portal-upload-required="portalUploadRequired"
                                           :readonly="readonly"
                                           :disable-assignment="!workItem || !workItem.initialized"
                                           :assignment-option="assignmentOption"
                                           :live-assignment-date="liveAssignmentDate"
                                           :live-assignment-start-time="liveAssignmentStartTime"
                                           :live-assignment-end-time="liveAssignmentEndTime"
                                           :turn-allocation-mode="(workItem && (workItem.turnAllocationMode != null)) ? workItem.turnAllocationMode : 10"
                                           :live-assignment-round-robin-mode="(workItem && (workItem.turnAllocationMode != null)) ? (workItem.turnAllocationMode == turnAllocationRoundRobinMode) : false"
                                           :delayed-assignment-assigned-audio-length-mode="(workItem && (workItem.turnAllocationMode != null)) ? (workItem.turnAllocationMode == turnAllocationAssignedAudioLengthMode) : false"
                                           :delayed-assignment-start-date="delayedAssignmentStartDate"
                                           :delayed-assignment-due-date="delayedAssignmentDueDate"
                                           :court-registry-id="courtRegistryId"
                                           :interview-organisation-id="interviewOrganisationId"
                                           :time-zone-id="timeZoneId"
                                           :default-live-assignment-response-time="defaultLiveAssignmentResponseTime"
                                           :default-delayed-assignment-response-time="defaultDelayedAssignmentResponseTime"
                                           :all-default-response-time-options="allDefaultResponseTimeOptions"
                                           :all-attachment-classification-options="mainAttachmentClassificationOptions"
                                           v-model="generalData"
                                           @modalOpen="setWorksheetReloadStatus">

                <template v-slot:required-date-time>
                    <b-form-group label="Due Date/Time"
                                  label-for="dueDateTimeUTC"
                                  class="col-md-2"
                                  v-if="generalData.permissions.canViewAllDetails">
                        <b-form-input name="dueDateTimeUTC"
                                      :value="dueDateTimeDisplayString"
                                      readonly>
                        </b-form-input>
                    </b-form-group>
                    <b-form-group label="Turns Due Date/Time"
                                  label-for="turnsDueDateTimeUTC"
                                  :class="generalData.permissions.canViewAllDetails ? 'col-md-2' : 'col-md-4'">
                        <b-input-group>
                            <b-form-input name="turnsDueDateTimeUTC"
                                          :value="turnsDueDateTimeDisplayString"
                                          readonly>
                            </b-form-input>
                            <b-input-group-append v-if="generalData.permissions.canInitialize && workItem && workItem.initialized && (workItem.mergedDateTimeUTC == null)">
                                <b-button variant="outline-secondary" v-b-tooltip title="Edit Turns Due Date/Time" v-on:click="editTurnsDueDateTime"><i class="fas fa-edit"></i></b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </template>
                <template v-slot:additional-information>
                    <b-form-row v-if="generalData.permissions.canViewAllDetails && workItem && workItem.expectedNumberOfAudioSeconds">
                        <b-form-group label="Expected Audio Length"
                                      label-for="expectedAudioLength"
                                      class="col-md-2">
                            <b-input-group>
                                <b-form-input name="totals"
                                              :value="formatSecondsAsDuration(workItem.expectedNumberOfAudioSeconds)"
                                              readonly>
                                </b-form-input>
                            </b-input-group>
                        </b-form-group>
                        <b-form-group label="Current End Time"
                                  label-for="currentEndTime"
                                  class="col-md-2">
                            <b-input-group>
                                <b-form-input name="currentEndTime"
                                              :value="(formatDateTime(workItem.endTime, 'h:mm:ss A', true, true))"
                                              readonly>
                                </b-form-input>
                                <b-input-group-append v-if="generalData.permissions.canManageTurns && workItem && workItem.initialized && (workItem.mergedDateTimeUTC == null)">
                                    <b-button variant="outline-secondary" v-b-tooltip title="Edit Current End Time" v-on:click="editEndTime"><i class="fas fa-edit"></i></b-button>
                                </b-input-group-append>
                            </b-input-group>
                        </b-form-group>
                        <b-form-group v-if="showTurnAllocationModes"
                                  label="Turn Allocation Mode"
                                  label-for="turnAllocationMode"
                                  class="col-md-3">
                            <b-input-group>
                                <b-form-input name="totals"
                                              :value="workItem.turnAllocationModeDisplayName"
                                              readonly>
                                </b-form-input>
                            </b-input-group>
                        </b-form-group>
                    </b-form-row>
                    <b-form-row v-if="workItem && workItem.mergedDateTimeUTC">
                        <b-form-group label="Merged"
                                      label-for="merged"
                                      class="col-md-4">
                            <b-form-input name="merged"
                                          :value="(formatDateTime(workItem.mergedDateTimeUTC, 'DD/MM/YYYY h:mm A') + ' by ' + workItem.mergedByUserAccount.displayName)"
                                          readonly>
                            </b-form-input>
                        </b-form-group>
                        <b-form-group label="Checked"
                                      label-for="checked"
                                      class="col-md-4"
                                      v-if="workItem.checkedDateTimeUTC">
                            <b-form-input name="checked"
                                          :value="(formatDateTime(workItem.checkedDateTimeUTC, 'DD/MM/YYYY h:mm A') + ' by ' + workItem.checkedByUserAccount.displayName)"
                                          readonly>
                            </b-form-input>
                        </b-form-group>
                        <b-form-group label="Completed"
                                      label-for="completed"
                                      class="col-md-4"
                                      v-if="workItem.completedDateTimeUTC">
                            <b-form-input name="completed"
                                          :value="(formatDateTime(workItem.completedDateTimeUTC, 'DD/MM/YYYY h:mm A') + ' by ' + workItem.completedByUserAccount.displayName)"
                                          readonly>
                            </b-form-input>
                        </b-form-group>
                    </b-form-row>
                    <b-form-row v-if="generalData.permissions.canViewAllDetails && workItem && workItem.mergedDateTimeUTC">
                        <b-form-group label="Totals"
                                      label-for="totals"
                                      class="col-md-6">
                            <b-input-group>
                                <b-form-input name="totals"
                                              :value="transcriptTotals"
                                              readonly>
                                </b-form-input>
                                <b-input-group-append v-if="generalData.permissions.canModifyPostCompletion && !readonly && !hasTopscribeDocuments">
                                    <b-button variant="outline-secondary" v-b-tooltip title="Edit Totals" v-on:click="openEditTranscriptTotalsDialog"><i class="fas fa-edit"></i></b-button>
                                </b-input-group-append>
                            </b-input-group>
                        </b-form-group>
                    </b-form-row>
                </template>

                <turn-transcript-workitems ref="turnTranscriptWorkItems"
                                           :api-url="apiBaseUrl"
                                           :file-download-url="fileDownloadUrl"
                                           :topscribe-editor-base-url="$urlHelper.getPageUrl('TOPScribe/Edit')"
                                           :topscribe-view-base-url="$urlHelper.getPageUrl('TOPScribe/View')"
                                           :transcript-work-item-id="workItem.workItemId"
                                           :user-account-id="currentUserAccountId"
                                           :turn-length="$config.defaultTurnLengthInMins"
                                           :has-topscribe-documents="hasTopscribeDocuments"
                                           :all-blank-audio-segment-reasons="allBlankAudioSegmentReasons"
                                           :turnTranscriptAttachmentClassificationOption="turnTranscriptAttachmentClassificationOption"
                                           :correctedTurnTranscriptAttachmentClassificationOption="correctedTurnTranscriptAttachmentClassificationOption"
                                           :turns-readonly="workItem.mergedDateTimeUTC != null"
                                           :readonly="workItem.checkedDateTimeUTC != null"
                                           v-on:change="onTurnTranscriptWorkItemsChange"
                                           :permissions="generalData.permissions"
                                           :assigned-staff="generalData.assignedStaff"
                                           v-if="workItem && workItem.initialized"
                                           @modalOpen="setWorksheetReloadStatus">
                </turn-transcript-workitems>

                <template v-slot:action-buttons>
                    <div class="btn-toolbar justify-content-end mt-2" role="toolbar" aria-label="Action buttons">
                        <b-input-group prepend="Transcript Delivery" class="mr-2">
                            <b-form-input :value="deliveryType.displayName"
                                          readonly
                                          class="bg-transparent">
                            </b-form-input>
                        </b-input-group>

                        <div class="btn-group" role="group">
                            <b-button variant="outline-secondary"
                                      key="initialize"
                                      v-on:click="openInitializeWorksheetDialog"
                                      v-if="generalData.permissions.canInitialize && workItem && !workItem.workStarted">
                                <i class="fas fa-cog mr-1"></i>
                                {{ !workItem.initialized ? 'Initialise' : 'Reconfigure' }}
                            </b-button>
                            <b-button variant="outline-secondary"
                                      key="merged"
                                      v-on:click="onProgressWorksheet('merged')"
                                      v-if="generalData.permissions.canMergeTranscript && progressWorksheetActionAvailable('merged')">
                                <i class="fas fa-paste mr-1"></i>Mark Merged
                            </b-button>
                            <b-button variant="outline-secondary"
                                      key="checked"
                                      v-on:click="onProgressWorksheet('checked')"
                                      v-if="generalData.permissions.canCheckTranscript && progressWorksheetActionAvailable('checked')">
                                <i class="fas fa-check mr-1"></i>Mark Checked
                            </b-button>
                            <b-button variant="outline-secondary"
                                      key="delivered"
                                      v-on:click="onProgressWorksheet('complete')"
                                      v-if="generalData.permissions.canDeliver && progressWorksheetActionAvailable('complete')">
                                <i class="fas fa-paper-plane mr-1"></i>{{ (deliveryType.enumValue == $enum.DeliveryType.Manual) ? 'Mark Delivered' : 'Deliver' }}
                            </b-button>
                            <b-button variant="outline-secondary"
                                      key="markuploaded"
                                      v-on:click="markUploaded"
                                      v-if="generalData.permissions.canMarkUploaded && portalUploadRequired && progressWorksheetActionAvailable('markuploaded')">
                                <i class="fas fa-upload mr-1"></i>Mark Uploaded
                            </b-button>
                            <b-button :href="returnUrl" variant="outline-secondary"><i class="fas fa-arrow-left mr-1"></i>Back</b-button>
                        </div>
                    </div>
                </template>
            </worksheet-general-information>

            <validation-observer v-slot="vo" slim>
                <b-modal size="lg"
                         centered
                         title="Initialise Worksheet"
                         v-model="initializeWorksheetData.dialogOpen"
                         :ok-disabled="busy"
                         v-on:ok.prevent="vo.handleSubmit(initializeWorksheet)"
                         v-on:hidden="initializeWorksheetDialogHidden">
                    <b-alert :show="((initializeWorksheetData.currentErrors != null) && (initializeWorksheetData.currentErrors.length > 0))" variant="danger" dismissible>
                        <div v-for="(error, index) in initializeWorksheetData.currentErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <b-form-row v-if="displayDueWarning">
                        <div class="alert alert-warning col-12" role="alert">
                            Warning: Turns Due is greater than the Worksheet Due Date/Time - {{ formatDateTime(workItem.dueDateTimeUTC, 'DD/MM/YYYY hh:mm A', true, false)}}
                        </div>
                    </b-form-row>
                    <b-form-row v-if="showTurnAllocationModeChangedWarning">
                        <div class="alert alert-warning col-12" role="alert">
                            Warning: You are about to change the turn allocation mode - all current assignments will be cleared!
                        </div>
                    </b-form-row>
                    <b-form-row>
                        <validation-provider mode="passive" rules="required" name="Start Time" v-slot="vp" slim>
                            <b-form-group label="Start Time"
                                          label-for="initializeWorksheetStartTime"
                                          class="col-md-3">
                                <b-form-input ref="initializeWorksheetStartTime"
                                              name="startTime"
                                              type="time"
                                              step="1"
                                              v-model="initializeWorksheetData.startTime"
                                              :state="vp.errors[0] ? false : null"
                                              autofocus>
                                </b-form-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <template v-if="relatedCourtSittingId">
                            <validation-provider mode="passive" rules="required" name="End Time" v-slot="vp" slim>
                                <b-form-group label="End Time"
                                              label-for="initializeWorksheetEndTime"
                                              class="col-md-3">
                                    <b-form-input name="initializeWorksheetEndTime"
                                                  type="time"
                                                  step="1"
                                                  v-model="initializeWorksheetData.endTime"
                                                  :state="vp.errors[0] ? false : null">
                                    </b-form-input>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <validation-provider rules="required" name="Turn Length" v-slot="vp" slim v-if="currentTurnAllocationModeRequiresTurns">
                                <b-form-group label="Turn Length"
                                              label-for="initializeWorksheetTurnLength"
                                              description="in minutes"
                                              class="col-md-2">
                                    <b-form-input name="initializeWorksheetTurnLength"
                                                  type="number"
                                                  min="5"
                                                  max="30"
                                                  v-model="initializeWorksheetData.turnLength"
                                                  :state="vp.errors[0] ? false : null">
                                    </b-form-input>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </template>
                        <template v-if="relatedInterviewId">
                            <validation-provider rules="required" name="Length of Interview" v-slot="vp" slim>
                                <b-form-group label="Length"
                                              label-for="interviewLength"
                                              class="col-md-3">
                                    <audio-length-input name="interviewLength"
                                                        v-model="initializeWorksheetData.lengthInSeconds"
                                                        :state="vp.errors[0] ? false : null">
                                    </audio-length-input>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <validation-provider rules="required" name="Turn Count" v-slot="vp" slim v-if="currentTurnAllocationModeRequiresTurns">
                                <b-form-group label="Turn Count"
                                              label-for="initializeWorksheetTurnCount"
                                              class="col-md-2">
                                    <b-form-input name="initializeWorksheetTurnCount"
                                                  type="number"
                                                  min="1"
                                                  max="100"
                                                  v-model="initializeWorksheetData.turnCount"
                                                  :state="vp.errors[0] ? false : null">
                                    </b-form-input>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </template>

                        <validation-provider :rules="turnsDueDateValidationRules" name="Turns Due" v-slot="vp" slim>
                            <b-form-group label="Turns Due"
                                          label-for="initializeWorksheetTurnsDueDateTimeUTC"
                                          class="col-md-4">
                                <ejs-datetimepicker name="initializeWorksheetTurnsDueDateTimeUTC"
                                                    v-model="initializeWorksheetData.turnsDueDateTimeUTC"
                                                    v-on:change="onInitialiseDueDateChange"
                                                    :min="minTurnsDueDate"
                                                    format="d/M/yyyy h:mma"></ejs-datetimepicker>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <template v-if="relatedCourtSittingId">
                            <validation-provider rules="required" name="Court Room" v-slot="vp" slim>
                                <b-form-group label="Court Room"
                                              label-for="initializeWorksheetCourtRoomId"
                                              class="col-md-8">
                                    <select2 name="initializeWorksheetCourtRoomId"
                                             placeholder="Select a room"
                                             enable-ajax
                                             in-b-modal
                                             key-property="courtRoomId"
                                             text-property="courtLocationRoomName"
                                             v-model="initializeWorksheetData.courtRoomId"
                                             :options="initializeWorksheetData.initialCourtRooms"
                                             v-on:search="findCourtRooms"
                                             :state="vp.errors[0] ? false : null">
                                    </select2>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <validation-provider rules="required" :name="$t('Entities.Shared.CourtSittingMatterType')" v-slot="vp" slim>
                                <b-form-group :label="$t('Entities.Shared.CourtSittingMatterType')"
                                              label-for="initializeWorksheetCourtSittingMatterType"
                                              class="col-md-4">
                                    <select2 name="initializeWorksheetCourtSittingMatterType"
                                             :placeholder="$t('Vue.Shared.Select2Placeholder.CourtSittingMatterType')"
                                             key-property="courtSittingMatterTypeId"
                                             text-property="name"
                                             v-model="initializeWorksheetData.courtSittingMatterTypeId"
                                             :options="allCourtSittingMatterTypes"
                                             in-b-modal
                                             :state="vp.errors[0] ? false : null">
                                    </select2>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </template>
                    </b-form-row>
                    <b-form-row>
                        <b-form-group label="Audio Assessment"
                                      label-for="initializeWorksheetDataAudioAssessment"
                                      class="col-md-4">
                            <select2 v-model="initializeWorksheetData.audioAssessment"
                                     placeholder="Select an audio assessment"
                                     :options="allAudioAssessmentOptions"
                                     disable-search
                                     in-b-modal>
                            </select2>
                        </b-form-group>
                        <validation-provider rules="required" name="Turn Allocation Mode" v-slot="vp" slim v-if="allTurnAllocationModeOptions && allTurnAllocationModeOptions.length > 1">
                            <b-form-group label="Turn Allocation Mode"
                                          label-for="initializeWorksheetTurnAllocationMode"
                                          class="col-md-4">
                                <select2 name="initializeWorksheetTurnAllocationMode"
                                         v-model="initializeWorksheetData.turnAllocationMode"
                                         placeholder="Select the turn allocation mode"
                                         :options="allTurnAllocationModeOptions"
                                         disable-search
                                         in-b-modal
                                         :state="vp.errors[0] ? false : null">
                                </select2>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>
                    <blank-audio-segments :blank-audio-segments="initializeWorksheetData.blankAudioSegments"
                                          :all-blank-audio-segment-reasons="allBlankAudioSegmentReasons">
                    </blank-audio-segments>
                </b-modal>
            </validation-observer>

            <validation-observer v-slot="vo" slim>
                <b-modal size="md"
                         centered
                         title="Edit Turns Due Date/Time"
                         v-model="editTurnsDueDateTimeDialogOpen"
                     v-on:ok.prevent="vo.handleSubmit(updateWorkItemTurnsDueDateTime)"
                     v-on:hidden="reload">
                    <b-alert :show="((currentTurnsDueDateTimeErrors != null) && (currentTurnsDueDateTimeErrors.length > 0))" variant="danger" dismissible>
                        <div v-for="(error, index) in currentTurnsDueDateTimeErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <b-form-row v-if="displayDueWarning">
                        <div class="alert alert-warning" role="alert">
                            Warning: Turns Due is greater than the Worksheet Due Date/Time - {{ formatDateTime(workItem.dueDateTimeUTC, 'DD/MM/YYYY hh:mm A', true, false)}}
                        </div>
                    </b-form-row>
                    <b-form-row v-if="displayPublicHolidayWarning">
                        <div class="alert alert-warning col-12" role="alert">
                            Warning: Turns Due Date Time is a public holiday
                        </div>
                    </b-form-row>
                    <b-form-row>
                        <validation-provider :rules="turnsDueDateValidationRules" name="Turns Due Date/Time" v-slot="vp" slim>
                            <b-form-group label="Turns Due Date/Time"
                                          label-for="currentTurnsDueDateTimeUTC"
                                          class="col">
                                <ejs-datetimepicker ref="currentTurnsDueDateTimeUTC"
                                                    name="currentTurnsDueDateTimeUTC"
                                                    v-model="currentTurnsDueDateTimeUTC"
                                                    v-on:change="onEditDueDateChange"
                                                    :min="minTurnsDueDate"
                                                    format="d/M/yyyy h:mma"></ejs-datetimepicker>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>
                </b-modal>
            </validation-observer>

            <validation-observer v-slot="vo" slim>
                <b-modal size="md"
                         centered
                         title="Edit Current End Time"
                         v-model="editWorkItemTurnsEndDialogOpen"
                     v-on:ok.prevent="vo.handleSubmit(updateWorkItemTurnsEndTime)"
                     v-on:hidden="reload">
                    <b-alert :show="((currentTurnsEndTimeErrors != null) && (currentTurnsEndTimeErrors.length > 0))" variant="danger" dismissible>
                        <div v-for="(error, index) in currentTurnsEndTimeErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <b-form-row>
                        <validation-provider mode="passive" rules="required" name="End Time" v-slot="vp" slim>
                            <b-form-group label="End Time"
                                          label-for="currentTurnsEndTime"
                                          class="col-md-6">
                                <b-form-input name="currentTurnsEndTime"
                                              type="time"
                                              step="1"
                                              v-model="currentTurnsEndTime"
                                              :state="vp.errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>
                </b-modal>
            </validation-observer>

            <validation-observer v-slot="vo" slim v-if="generalData.permissions.canMergeTranscript || generalData.permissions.canCheckTranscript || generalData.permissions.canDeliver">
                <b-modal size="xl"
                         :title="progressWorksheetDialogTitle"
                         v-model="progressWorksheetData.dialogOpen"
                         v-on:ok.prevent="vo.handleSubmit(progressWorksheet)"
                         v-on:hidden="reload"
                         :ok-disabled="showNoTranscriptAlert">
                
                    <b-alert :show="(progressWorksheetData.currentErrors != null) && (progressWorksheetData.currentErrors.length > 0)" variant="danger" dismissible>
                        <div v-for="(error, index) in progressWorksheetData.currentErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <template v-if="progressWorksheetData.action != 'complete'">
                        <validation-provider rules="required|integer|min_value:0" name="Number of Words" v-slot="vp" slim v-if="!hasTopscribeDocuments && $config.transcriptCompletion.enableWordCount" mode="lazy">
                            <b-form-invalid-feedback class="col" :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            <b-form-group label="Number of Words"
                                          label-for="numberOfWords"
                                          class="col"
                                          label-cols-md="8">
                                <b-form-input name="numberOfWords"
                                              type="text"
                                              min="0"
                                              v-model="progressWorksheetData.numberOfWords"
                                              :state="vp.errors[0] ? false : null"
                                              autofocus>
                                </b-form-input>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider rules="integer|min_value:0" name="Words to Exclude" v-slot="vp" slim v-if="!hasTopscribeDocuments && $config.transcriptCompletion.enableWordsToExclude">
                            <b-form-invalid-feedback class="col" :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            <b-form-group label="Words to Exclude"
                                          label-for="wordsToExclude"
                                          class="col"
                                          label-cols-md="8">
                                <b-form-input name="wordsToExclude"
                                              type="text"
                                              min="0"
                                              v-model="progressWorksheetData.wordsToExclude"
                                              :state="vp.errors[0] ? false : null">

                                </b-form-input>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider rules="required|integer|min_value:0" name="Number of Pages" v-slot="vp" slim v-if="!hasTopscribeDocuments && $config.transcriptCompletion.enablePageCount">
                            <b-form-invalid-feedback class="col" :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            <b-form-group label="Number of Pages"
                                          label-for="numberOfPages"
                                          class="col"
                                          label-cols-md="8">
                                <b-form-input name="numberOfPages"
                                              type="text"
                                              min="0"
                                              v-model="progressWorksheetData.numberOfPages"
                                              :state="vp.errors[0] ? false : null">
                                </b-form-input>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider rules="integer|min_value:0" name="Pages to Exclude" v-slot="vp" slim v-if="!hasTopscribeDocuments && $config.transcriptCompletion.enablePagesToExclude">
                            <b-form-invalid-feedback class="col" :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            <b-form-group label="Pages to Exclude"
                                          label-for="pagesToExclude"
                                          class="col"
                                          label-cols-md="8">
                                <b-form-input name="pagesToExclude"
                                              type="text"
                                              min="0"
                                              v-model="progressWorksheetData.pagesToExclude"
                                              :state="vp.errors[0] ? false : null">

                                </b-form-input>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider rules="required|min_value:0" name="Audio Length" v-slot="vp" slim
                                             v-if="$config.transcriptCompletion.enableAudioMinutes">
                            <b-form-invalid-feedback class="col" :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            <b-form-group label="Audio Length"
                                          label-for="audioLength"
                                          class="col"
                                          label-cols-md="8">
                                <audio-length-input name="audioLength"
                                                    v-model="progressWorksheetData.numberOfAudioSeconds"
                                                    :state="vp.errors[0] ? false : null">
                                </audio-length-input>
                            </b-form-group>
                        </validation-provider>
                    </template>
                    <template v-if="progressWorksheetData.action == 'complete'">
                        <template v-if="deliveryType.enumValue == $enum.DeliveryType.Manual">
                            <p>Are you sure you want to mark the CHECKED orders as delivered?<br><b>This action cannot be undone.</b></p>
                        </template>
                        <template v-else>
                            <p>Are you sure you want to deliver the CHECKED orders?<br><b>This action cannot be undone.</b></p>
                        </template>
                        <workitem-orders :api-base-url="apiBaseUrl"
                                         :work-item-id="workItem.workItemId"
                                         for-order-delivery
                                         v-model="progressWorksheetData.ordersToBeDelivered"
                                         :all-audio-sub-status-options="allAudioSubStatusOptions"
                                         :can-view-turnarounds="generalData.permissions.canViewTurnarounds"
                                         :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                                         :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                                         :all-deposit-sub-status-options="allDepositSubStatusOptions"
                                         :all-vetting-sub-status-options="allVettingSubStatusOptions"
                                         :all-delivered-late-reason-options="allDeliveredLateReasonOptions"
                                         @modalOpen="setWorksheetReloadStatus"
                                         @showNoTranscriptAlert="noTranscriptAlertActive">
                        </workitem-orders>
                    </template>
                </b-modal>
            </validation-observer>

            <b-modal size="md"
                     centered
                     :title="progressWorksheetDialogTitle"
                     ok-title="Yes"
                     v-model="progressWorksheetData.confirmationDialogOpen"
                    v-on:ok.prevent="progressWorksheet"
                    v-on:hidden="reload">

                <b-alert :show="(progressWorksheetData.currentErrors != null) && (progressWorksheetData.currentErrors.length > 0)" variant="danger" dismissible>
                    <div v-for="(error, index) in progressWorksheetData.currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <p>Are you sure you want to {{ progressWorksheetDialogAction }}?</p>
                <p>This action cannot be undone!</p>
            </b-modal>

            <validation-observer v-slot="vo" slim v-if="generalData.permissions.canViewAllDetails">
                <b-modal size="md"
                         centered
                         title="Update Transcript Totals"
                         v-model="editTranscriptTotalsData.dialogOpen"
                     v-on:ok.prevent="vo.handleSubmit(updateTranscriptTotals)"
                     v-on:hidden="reload">
                    <b-alert :show="((editTranscriptTotalsData.currentErrors != null) && (editTranscriptTotalsData.currentErrors.length > 0))" variant="danger" dismissible>
                        <div v-for="(error, index) in editTranscriptTotalsData.currentErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <b-form-row>
                        <validation-provider rules="required|integer|min_value:0" name="Number of Words" v-slot="vp" slim
                                             v-if="$config.transcriptCompletion.enableWordCount">
                            <b-form-group label="Number of Words"
                                          label-for="numberOfWords"
                                          class="col">
                                <b-form-input name="numberOfWords"
                                              type="text"
                                              min="0"
                                              v-model="editTranscriptTotalsData.numberOfWords"
                                              :state="vp.errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>
                    <b-form-row>
                        <validation-provider rules="integer|min_value:0" name="Words to Exclude" v-slot="vp" slim v-if="$config.transcriptCompletion.enableWordsToExclude">
                            <b-form-group label="Words to Exclude"
                                          label-for="wordsToExclude"
                                          class="col">
                                <b-form-input name="wordsToExclude"
                                              type="text"
                                              min="0"
                                              v-model="editTranscriptTotalsData.wordsToExclude"
                                              :state="vp.errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>
                    <b-form-row>
                        <validation-provider rules="required|integer|min_value:0" name="Number of Pages" v-slot="vp" slim
                                             v-if="$config.transcriptCompletion.enablePageCount">
                            <b-form-group label="Number of Pages"
                                          label-for="numberOfPages"
                                          class="col">
                                <b-form-input name="numberOfPages"
                                              type="text"
                                              min="0"
                                              v-model="editTranscriptTotalsData.numberOfPages"
                                              :state="vp.errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>

                    <b-form-row>
                        <validation-provider rules="integer|min_value:0" name="Pages to Exclude" v-slot="vp" slim v-if="$config.transcriptCompletion.enablePagesToExclude">
                            <b-form-group label="Pages to Exclude"
                                          label-for="pagesToExclude"
                                          class="col">
                                <b-form-input name="pagesToExclude"
                                              type="text"
                                              min="0"
                                              v-model="editTranscriptTotalsData.pagesToExclude"
                                              :state="vp.errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>

                    <b-form-row>
                        <validation-provider rules="required|min_value:0" name="Audio Length" v-slot="vp" slim
                                             v-if="$config.transcriptCompletion.enableAudioMinutes">
                            <b-form-group label="Audio Length"
                                          label-for="audioLength"
                                          class="col">
                                <audio-length-input name="audioLength"
                                                    v-model="editTranscriptTotalsData.numberOfAudioSeconds"
                                                    :state="vp.errors[0] ? false : null">
                                </audio-length-input>
                                <b-form-invalid-feedback class="col" :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>
                </b-modal>
            </validation-observer>
        </b-overlay>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import Visibility from 'visibilityjs';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required, integer, min_value, double } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    import min_date from '../../vee-validate-rules/min_date';
    import custom_rules_en from '../../vee-validate-rules/locale/en.js';
    extend('required', required);
    extend('integer', integer);
    extend('min_value', min_value);
    extend('double', double);
    extend('min_date', min_date);
    localize('en', en);
    localize('en', custom_rules_en);

    import AudioLengthInput from '../AudioLengthInput';

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import TranscriptMixins from '../../mixins/TranscriptMixins';
    import UuidMixins from '../../mixins/UuidMixins';
    import WorksheetGeneralInformation from './WorksheetGeneralInformation.vue'
    import TurnTranscriptWorkitems from '../TurnTranscriptWorkitems.vue'

    Vue.use(BootstrapVue);
    Vue.component('audio-length-input', AudioLengthInput);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('worksheet-general-information', WorksheetGeneralInformation);
    Vue.component('turn-transcript-workitems', TurnTranscriptWorkitems);

    export default {
        name: 'TranscriptWorksheet',
        mixins: [ErrorsMixin, DateTimeMixin, TranscriptMixins, UuidMixins],

        props: {
            returnUrl: {
                type: String,
                required: true
            },
            apiBaseUrl: {
                type: String,
                required: true
            },
            imagesUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            workItemId: {
                type: String,
                required: true
            },
            relatedCourtSittingId: {
                type: String,
                default: null
            },
            relatedCourtMatterId: {
                type: String,
                default: null
            },
            relatedInterviewId: {
                type: String,
                default: null
            },
            relatedListCourtId: {
                type: String,
                default: null
            },
            assignmentRelevantRoles: {
                type: Array,
                required: true
            },
            assignmentRolesWithAudioMinutes: {
                type: Array,
                required: true
            },
            assignmentRolesWithRoundRobinPriority: {
                type: Array,
                required: true
            },
            portalUploadRequired: {
                type: Boolean,
                default: false,
            },
            initialPermissions: {
                type: Object,
                default: null
            },
            refreshIntervalInMilliseconds: {
                type: Number,
                default: 5000
            },
            allAudioSubStatusOptions: {
                type: Array,
                default: null
            },
            allLogNotesSubStatusOptions: {
                type: Array,
                default: null
            },
            allReleaseApprovalSubStatusOptions: {
                type: Array,
                default: null
            },
            allDepositSubStatusOptions: {
                type: Array,
                default: null
            },
            allVettingSubStatusOptions: {
                type: Array,
                default: null
            },
            allDeliveredLateReasonOptions: {
                type: Array,
                default: null
            },
            allCourtSittingMatterTypes: {
                type: Array,
                default: null
            },
            allAudioAssessmentOptions: {
                type: Array,
                default: null
            },
            allTurnAllocationModeOptions: {
                type: Array,
                default: null
            },
            allAudioQualityOptions: {
                type: Array,
                default: null
            },
            turnAllocationModesWithoutTurns: {
                type: Array,
                default: null
            },
            turnAllocationRoundRobinMode: {
                type: Number,
                default: null
            },
            turnAllocationAssignedAudioLengthMode: {
                type: Number,
                default: null
            },
            readonly: {
                type: Boolean,
                default: false
            },
            assignmentOption: {
                type: String,
                required: false
            },
            liveAssignmentDate: {
                type: Date,
                required: false,
            },
            liveAssignmentStartTime: {
                type: Date,
                required: false,
            },
            liveAssignmentEndTime: {
                type: Date,
                required: false,
            },
            delayedAssignmentStartDate: {
                type: Date,
                required: false,
            },
            delayedAssignmentDueDate: {
                type: Date,
                required: false,
            },
            courtRegistryId: {
                type: String,
                required: false,
                default: null
            },
            interviewOrganisationId: {
                type: String,
                required: false,
                default: null
            },
            timeZoneId: {
                type: String,
                required: false,
                default: null
            },
            defaultLiveAssignmentResponseTime: {
                type: Number
            },
            defaultDelayedAssignmentResponseTime: {
                type: Number
            },
            allBlankAudioSegmentReasons: {
                type: Array,
                required: true
            },
            allDefaultResponseTimeOptions: {
                type: Array,
                default: null
            },
            mainAttachmentClassificationOptions: {
                type: Array,
                default: null
            },
            turnTranscriptAttachmentClassificationOption: {
                type: Object,
                default: null
            },
            correctedTurnTranscriptAttachmentClassificationOption: {
                type: Object,
                default: null
            },
            deliveryType: {
                type: Object,
                default: null
            }
        },
        data: function () {
            return {
                worksheetReloadStatus: false, // flag used to indicate if a modal is open and to pause auto reload()
                customMessages: {
                    audioMinutes: {
                        double: 'must be a number with only 1 decimal'
                    }

                },
                generalData: {
                    permissions: this.initialPermissions ? this.initialPermissions : {},
                    assignedStaff: []
                },
                workItem: null,
                initializeWorksheetData: {
                    startTime: null,
                    endTime: null,
                    lengthInSeconds: null,
                    turnLength: null,
                    turnCount: null,
                    courtRoomId: null,
                    initialCourtRooms: null,
                    courtSittingMatterTypeId: null,
                    audioAssessment: null,
                    turnAllocationMode: null,
                    turnsDueDateTimeUTC: null,
                    dialogOpen: false,
                    currentErrors: [],
                    blankAudioSegments: []
                },
                currentTurnsDueDateTimeUTC: null,
                editTurnsDueDateTimeDialogOpen: false,
                currentTurnsDueDateTimeErrors: [],

                currentTurnsEndTime: null,
                editWorkItemTurnsEndDialogOpen: false,
                currentTurnsEndTimeErrors: [],

                incompleteTurns: 0,
                progressWorksheetData: {
                    numberOfWords: null,
                    wordsToExclude: null,
                    pagesToExclude: null,
                    numberOfAudioSeconds: null,
                    numberOfPages: null,
                    ordersToBeDelivered: null,
                    action: null,
                    dialogOpen: false,
                    confirmationDialogOpen: false,
                    currentErrors: []
                },
                editTranscriptTotalsData: {
                    numberOfWords: null,
                    wordsToExclude: null,
                    pagesToExclude: null,
                    numberOfAudioSeconds: null,
                    numberOfPages: null,
                    dialogOpen: false,
                    currentErrors: null
                },
                timer: null,
                reloadPending: false,
                displayDueWarning: false,
                displayPublicHolidayWarning: false,
                mergeTurnTotals: null,
                busy: false,

                showNoTranscriptAlert: false,
            };
        },
        computed: {
            minTurnsDueDate: function () {
                var minDate = this.setTimeComponents(this.now(), 0);
                if (this.workItem && this.workItem.relatedTo.courtSitting) {
                    var sittingDate = this.setTimeComponents(this.parseDateTime(this.workItem.relatedTo.courtSitting.sittingDate, null, false, false));
                    if (minDate < sittingDate) minDate = sittingDate;
                } else if (this.workItem && this.workItem.relatedTo.interview) {
                    var interviewDate = this.setTimeComponents(this.parseDateTime(this.workItem.relatedTo.interview.interviewDateTime, null, false, false));
                    if (minDate < interviewDate) minDate = interviewDate;
                }
                return minDate;
            },
            turnsDueDateValidationRules: function () {
                return {
                    required: true,
                    min_date: {
                        min: this.minTurnsDueDate
                    }
                };
            },
            currentTurnsEndTimeValidationRules: function () {
                return {
                    required: true,
                    min_date: {
                        min: this.minTurnsDueDate
                    }
                };
            },
            dueDateTimeDisplayString: function () {
                if (this.workItem == null) return null;
                if (this.workItem.dueDateTimeUTC == null) return null;
                return this.formatDateTime(this.workItem.dueDateTimeUTC, 'DD/MM/YYYY h:mm A', true, false);
            },
            turnsDueDateTimeDisplayString: function () {
                if (this.workItem == null) return null;
                if (this.workItem.turnsDueDateTimeUTC == null) return null;
                return this.formatDateTime(this.workItem.turnsDueDateTimeUTC, 'DD/MM/YYYY h:mm A', true, false);
            },
            statusDisplayString: function () {
                if (this.workItem == null) return null;
                var statusDisplayString = this.workItem.statusDisplayName;
                if (this.portalUploadRequired) {
                    if (this.workItem.portalUploadStatus != null) {
                        statusDisplayString += " / ";
                        if (this.workItem.portalUploadStatus) statusDisplayString += this.$t("Entities.PortalUploadStatus.Uploaded");
                        else statusDisplayString += this.$t("Entities.PortalUploadStatus.NotUploaded");
                    }
                }
                return statusDisplayString;
            },
            hasTopscribeDocuments: function () {
                return ((this.workItem != null) && (this.workItem.topscribeDocumentId != null));
            },
            transcriptTotals: function () {
                var totals = '';
                if (this.workItem && this.workItem.mergedDateTimeUTC) {
                    totals = this.workItem.numberOfWords + ' words ';
                    if (this.workItem.wordsToExclude) {
                        totals += '(' + this.workItem.wordsToExclude + ' words excluded)';
                    }
                    totals += '/ ' + this.getNumberOfFolios(this.workItem.numberOfWords) + ' folios';
                    if (this.workItem.numberOfPages) {
                        totals += ' on ' + this.workItem.numberOfPages + ' pages';
                    }
                    if (this.workItem.pagesToExclude) {
                        totals += ' (' + this.workItem.pagesToExclude + ' pages excluded)';
                    }
                    if (this.workItem.numberOfAudioSeconds) {
                        var audioTimeDisplay = this.formatSecondsAsDuration(this.workItem.numberOfAudioSeconds);
                        totals += ' for ' + audioTimeDisplay;
                    }
                }
                return totals;
            },
            progressWorksheetDialogTitle: function () {
                if (this.progressWorksheetData.action == 'merged') return 'Mark Transcript Merged';
                if (this.progressWorksheetData.action == 'checked') return 'Mark Transcript Checked';
                if (this.progressWorksheetData.action == 'complete') {
                    if (this.deliveryType.enumValue == this.$enum.DeliveryType.Manual) return 'Mark Transcript Delivered';
                    return 'Deliver Transcript';
                }
                return null;
            },
            progressWorksheetDialogAction: function () {
                if (this.progressWorksheetData.action == 'merged') return 'mark the transcript merged';
                if (this.progressWorksheetData.action == 'checked') return 'mark the transcript checked';
                if (this.progressWorksheetData.action == 'complete') return 'mark the transcript delivered';
                return null;
            },
            currentTurnAllocationModeRequiresTurns: function () {
                if (this.initializeWorksheetData.turnAllocationMode == null) return false;
                if (!this.turnAllocationModesWithoutTurns) return true;
                if (this.turnAllocationModesWithoutTurns.find(value => value == this.initializeWorksheetData.turnAllocationMode) != null) return false;
                return true;
            },
            showTurnAllocationModes: function () {
                return this.allTurnAllocationModeOptions.length > 1;
            },
            showTurnAllocationModeChangedWarning: function () {
                if (!this.workItem) return false;
                if (!this.workItem.hasActiveOrPendingAssignments) return false;
                if (this.initializeWorksheetData.previousTurnAllocationMode == null) return false;
                return (this.initializeWorksheetData.previousTurnAllocationMode != this.initializeWorksheetData.turnAllocationMode);
            }
        },
        methods: {
            setWorksheetReloadStatus: function (anyInnerModalOpen) {
                this.worksheetReloadStatus = anyInnerModalOpen;
                this.reload();
            },
            noTranscriptAlertActive: function (active) {
                this.showNoTranscriptAlert = active;
            },

            getColorVariant: function (row) {

                switch (row.deliveryState) {
                    case 'complete': return 'text-success';
                    case 'incomplete': return 'text-danger';
                    default: return '';
                }
            },

            reload: function (reloadSubComponents = true) {
                if (this.timer != null) {
                    Visibility.stop(this.timer);
                    this.timer = null;
                }
                if (this.reloadPending) return;
                this.reloadPending = true;
                var initialized = this.workItem && this.workItem.initialized;
                var promises = [];
                promises.push(this.loadWorkItem());
                //load blank audio segments if not initialized
                if (!this.blankAudioSegmentsIntialized) {
                    promises.push(this.initializeBlankAudioSegments());
                    this.blankAudioSegmentsIntialized = true;
                }
                if (reloadSubComponents) {
                    if (initialized && this.$refs.turnTranscriptWorkItems) promises.push(this.$refs.turnTranscriptWorkItems.reload());
                    if (this.$refs.worksheetGeneralInformation) promises.push(this.$refs.worksheetGeneralInformation.reload());
                }
                Promise
                    .all(promises)
                    .then(() => {
                        this.reloadPending = false;
                    
                        // if ANY of the modals is open stop reloading, the v-on:hidden method in the modal resumes reloading by calling this.reload()
                        // first part are all the v-models of b-modals in this component ...
                        if (!this.initializeWorksheetData.dialogOpen &&
                            !this.editTurnsDueDateTimeDialogOpen &&
                            !this.progressWorksheetData.dialogOpen &&
                            !this.progressWorksheetData.confirmationDialogOpen &&
                            !this.editTranscriptTotalsData.dialogOpen &&
                            !this.editWorkItemTurnsEndDialogOpen &&
                            // ... plus and overall status flag for all sub components with their modals and dialogs
                            !this.worksheetReloadStatus) {
                            //console.log('reloading');
                            this.timer = Visibility.every(this.refreshIntervalInMilliseconds, this.reload);
                        } else {
                            //console.log('reloading PAUSED');
                        }
                    })
            },
            loadTurnTotals: function () {
                var url = this.$urlHelper.getApiUrl('workitem', 'loadturntotals', [this.workItemId]);
                return axios
                    .get(url)
                    .then(response => {
                        this.mergeTurnTotals = response.data;
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
               
            },
            loadWorkItem: function () {
                var url = this.$urlHelper.getApiUrl('workitem', this.workItemId);
                return axios
                    .get(url)
                    .then(response => {
                        this.workItem = response.data;
                        if (this.workItem.inProgressSinceDateTimeUTC) this.workItem.inProgressSinceDateTimeUTC = this.parseDateTime(this.workItem.inProgressSinceDateTimeUTC, null, true, true)
                        if (this.workItem.requiredDateTimeUTC) this.workItem.requiredDateTimeUTC = this.parseDateTime(this.workItem.requiredDateTimeUTC, null, true, true)
                        if (this.workItem.dueDateTimeUTC) this.workItem.dueDateTimeUTC = this.parseDateTime(this.workItem.dueDateTimeUTC, null, true, true)
                        if (this.workItem.turnsDueDateTimeUTC) this.workItem.turnsDueDateTimeUTC = this.parseDateTime(this.workItem.turnsDueDateTimeUTC, null, true, true)
                        if (this.workItem.lastUpdateDateTimeUTC) this.workItem.lastUpdateDateTimeUTC = this.parseDateTime(this.workItem.lastUpdateDateTimeUTC, null, true, true)
                        if (this.workItem.mergedDateTimeUTC) this.workItem.mergedDateTimeUTC = this.parseDateTime(this.workItem.mergedDateTimeUTC, null, true, true)
                        if (this.workItem.checkedDateTimeUTC) this.workItem.checkedDateTimeUTC = this.parseDateTime(this.workItem.checkedDateTimeUTC, null, true, true)
                        if (this.workItem.uploadedDateTimeUTC) this.workItem.uploadedDateTimeUTC = this.parseDateTime(this.workItem.uploadedDateTimeUTC, null, true, true)
                        if (this.workItem.completedDateTimeUTC) this.workItem.completedDateTimeUTC = this.parseDateTime(this.workItem.completedDateTimeUTC, null, true, true)
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },

            initializeBlankAudioSegments: function () {
                var url = this.$urlHelper.getApiUrl('blankaudiosegment', this.workItemId, ['initialize']);
                var currentErrors = [];
                axios
                    .get(url)
                    .then((response) => {
                        this.initializeWorksheetData.blankAudioSegments = response.data;
                    })
                    .catch((ex) => {
                        currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        }
                    });
            },

            openInitializeWorksheetDialog: function () {
                if (this.workItem.relatedTo.courtSitting) {
                    this.initializeWorksheetData.startTime = this.formatDateTime(this.workItem.relatedTo.courtSitting.startTime, 'HH:mm:ss', true, true);
                    this.initializeWorksheetData.endTime = this.formatDateTime(this.workItem.relatedTo.courtSitting.endTime, 'HH:mm:ss', true, true);
                    this.initializeWorksheetData.lengthInSeconds = null;
                    this.initializeWorksheetData.turnLength = this.$config.defaultTurnLengthInMins;
                    this.initializeWorksheetData.turnCount = null;
                    this.initializeWorksheetData.courtRoomId = this.workItem.relatedTo.courtSitting.courtRoomId;
                    if (this.workItem.relatedTo.courtSitting.courtRoom) {
                        this.initializeWorksheetData.initialCourtRooms = [this.workItem.relatedTo.courtSitting.courtRoom];
                    } else {
                        this.initializeWorksheetData.initialCourtRooms = null;
                    }
                    this.initializeWorksheetData.courtSittingMatterTypeId = this.workItem.relatedTo.courtSitting.courtSittingMatterTypeId;
                    this.initializeWorksheetData.audioAssessment = this.workItem.relatedTo.courtSitting.audioAssessment;
                } else if (this.workItem.relatedTo.interview) {
                    this.initializeWorksheetData.startTime = this.formatDateTime(this.workItem.relatedTo.interview.interviewDateTime, 'HH:mm:ss', true, true);
                    this.initializeWorksheetData.endTime = null;
                    this.initializeWorksheetData.lengthInSeconds = this.workItem.relatedTo.interview.lengthInSeconds;
                    this.initializeWorksheetData.turnLength = null;
                    this.initializeWorksheetData.turnCount = 1;
                    this.initializeWorksheetData.courtRoomId = null;
                    this.initializeWorksheetData.initialCourtRooms = null;
                    this.initializeWorksheetData.courtSittingMatterTypeId = null;
                    this.initializeWorksheetData.audioAssessment = this.workItem.relatedTo.interview.audioAssessment;
                }
                this.initializeWorksheetData.turnsDueDateTimeUTC = this.workItem.turnsDueDateTimeUTC;
                if (!this.initializeWorksheetData.turnsDueDateTimeUTC) {
                    this.initializeWorksheetData.turnsDueDateTimeUTC = this.addHours(this.workItem.dueDateTimeUTC, -2);
                }
                var now = this.now();
                if (this.initializeWorksheetData.turnsDueDateTimeUTC < now) {
                    var roundUpTo = roundTo => x => Math.ceil(x / roundTo) * roundTo;
                    var roundUpTo15Minutes = roundUpTo(1000 * 60 * 15);
                    var msUp = new Date(roundUpTo15Minutes(now));

                    this.initializeWorksheetData.turnsDueDateTimeUTC = msUp;
                }
                this.initializeWorksheetData.turnAllocationMode = this.workItem.turnAllocationMode;
                if ((this.initializeWorksheetData.turnAllocationMode == null) && this.allTurnAllocationModeOptions && (this.allTurnAllocationModeOptions.length == 1)) {
                    this.initializeWorksheetData.turnAllocationMode = this.allTurnAllocationModeOptions[0].value;
                }
                this.initializeWorksheetData.previousTurnAllocationMode = this.initializeWorksheetData.turnAllocationMode;
                this.initializeWorksheetData.dialogOpen = true;
            },

            findCourtRooms: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.$urlHelper.getApiUrl('courtroom', 'bycourtregistry', [this.courtRegistryId, 'find']);
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = mappedData.map((obj) => {
                                obj.id = obj.courtRoomId;
                                obj.text = obj.courtLocationRoomName;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            initializeWorksheet: function () {
                this.busy = true;
                var data = {
                    ...this.initializeWorksheetData
                };

                data.blankAudioSegments = [];

                this.initializeWorksheetData.blankAudioSegments.forEach(blankAudioSegment => {
                    data.blankAudioSegments.push({
                        //this is to make sure that we pass datetime strings without timezone conversion to the server - if we let JSON.stringify to the conversion it will convert everything to UTC
                        startTime: this.formatDateTime(blankAudioSegment.startTime, 'YYYY-MM-DDTHH:mm:ss', false, false),
                        endTime: this.formatDateTime(blankAudioSegment.endTime, 'YYYY-MM-DDTHH:mm:ss', false, false),
                        reason: blankAudioSegment.reason
                    });
                });

                data.startTime = this.parseDateTime(data.startTime, 'HH:mm:ss');
                if (data.endTime == null) delete data.endTime
                else data.endTime = this.parseDateTime(data.endTime, 'HH:mm:ss');
                if (data.lengthInSeconds == null) delete data.lengthInSeconds
                if (data.turnLength == null) delete data.turnLength
                if (data.turnCount == null) delete data.turnCount
                if (data.courtSittingMatterTypeId == null) delete data.courtSittingMatterTypeId;
                if (data.courtRoomId == null) delete data.courtRoomId;
                if (data.ausioAssessment == null) delete data.ausioAssessment;
                delete data.initialCourtRooms;
                delete data.dialogOpen;
                delete data.currentErrors;
                this.initializeWorksheetData.currentErrors = [];
                var url = this.$urlHelper.getApiUrl('workitem', 'transcript', [this.workItemId, 'initialize']);
                var thisVueApp = this;
                axios
                    .put(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function () {
                        thisVueApp.busy = false;
                        thisVueApp.reload();
                        thisVueApp.initializeWorksheetData.dialogOpen = false;
                    })
                    .catch(function (ex) {
                        thisVueApp.busy = false;
                        thisVueApp.initializeWorksheetData.currentErrors = thisVueApp.handleAxiosException(ex);
                    });
            },

            initializeWorksheetDialogHidden: function () {
                this.initializeWorksheetData.startTime = this.parseDateTime(this.initializeWorksheetData.startTime, 'HH:mm', true, true);
                this.initializeWorksheetData.endTime = this.parseDateTime(this.initializeWorksheetData.endTime, 'HH:mm', true, true);
                this.reload();
            },
            
            editTurnsDueDateTime: function () {
                this.currentTurnsDueDateTimeUTC = this.workItem.turnsDueDateTimeUTC;
                this.currentTurnsDueDateTimeErrors = [];
                this.editTurnsDueDateTimeDialogOpen = true;
            },
            updateWorkItemTurnsDueDateTime: function () {
                var url = this.$urlHelper.getApiUrl('workitem', 'transcript', [this.workItemId, 'turnsdue']);
                var turnsDueDateTimeUTC = this.currentTurnsDueDateTimeUTC;
                this.currentTurnsDueDateTimeErrors = [];
                var thisVueApp = this;
                axios
                    .put(url, JSON.stringify(turnsDueDateTimeUTC),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        thisVueApp.workItem.turnsDueDateTimeUTC = response.data;
                        thisVueApp.$bvToast.toast('Turns Due Date/Time updated successfully.', {
                            title: 'Update Successful',
                            variant: 'success',
                            appendToast: true
                        });
                    })
                    .catch(function (ex) {
                        thisVueApp.currentTurnsDueDateTimeErrors = thisVueApp.handleAxiosException(ex);
                    })
                    .finally(function () {
                        if (thisVueApp.currentTurnsDueDateTimeErrors.length == 0) thisVueApp.editTurnsDueDateTimeDialogOpen = false;
                    });
            },

            editEndTime: function () {
                this.currentTurnsEndTime = null; // this.formatDateTime(this.workItem.endTime, 'dd/MM/yyyy h:mm:ss A', true, true);
                this.currentTurnsEndTimeErrors = [];
                this.editWorkItemTurnsEndDialogOpen = true;
            },
            updateWorkItemTurnsEndTime: function () {
                if (this.currentTurnsEndTime == null) { return; }
                var data = this.currentTurnsEndTime;
                this.currentTurnsEndTimeErrors = [];

                var url = this.$urlHelper.getApiUrl('workitem','turntranscript', [this.workItemId, 'updateEndTime']);
                console.log(url);
                axios
                    .post(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentTurnsEndTimeErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentTurnsEndTimeErrors.length == 0) {
                            this.editWorkItemTurnsEndDialogOpen = false;
                        }
                    });
            },

            onTurnTranscriptWorkItemsChange: function (incompleteTurns) {
                this.incompleteTurns = incompleteTurns;
                this.reload(false);
            },
            progressWorksheetActionAvailable: function (action) {
                if (!this.workItem) return false;
                if (!this.workItem.initialized) return false;
                if (this.workItem.completedDateTimeUTC) return false;
                if (this.incompleteTurns != 0) return false;
                if ((action == 'complete') && (!this.workItem.checkedDateTimeUTC || !this.workItem.deliveryPending)) return false;
                if ((action == 'markuploaded') && (!this.workItem.checkedDateTimeUTC || this.workItem.uploadedDateTimeUTC)) return false;
                if ((action == 'checked') && (this.workItem.checkedDateTimeUTC || !this.workItem.mergedDateTimeUTC)) return false;
                if ((action == 'merged') && this.workItem.mergedDateTimeUTC) return false;
                if ((action == 'merged') && (this.workItem.status != this.$enum.WorkItemStatus.ReadyToMerge)) return false;
                //if (this.isAdministrator || this.isCoordinator) return true;
                if (!this.workItem.inProgressByUserAccountId) return true;
                return (this.workItem.inProgressByUserAccountId == this.currentUserAccountId);
            },
            onProgressWorksheet: function (action) {
                if (action == 'merged') {
                    Promise
                        .all([this.loadTurnTotals()])
                        .then(() => {
                            this.progressWorksheetData.numberOfWords = this.mergeTurnTotals.numberOfWords;
                            this.progressWorksheetData.numberOfPages = this.mergeTurnTotals.numberOfPages;
                            this.progressWorksheetData.numberOfAudioSeconds = this.mergeTurnTotals.numberOfAudioSeconds;
                            this.progressWorksheetData.wordsToExclude = this.mergeTurnTotals.wordsToExclude;
                            this.progressWorksheetData.pagesToExclude = this.mergeTurnTotals.pagesToExclude;
                        });
                } else {
                    this.progressWorksheetData.numberOfWords = this.workItem.numberOfWords;
                    this.progressWorksheetData.numberOfPages = this.workItem.numberOfPages;
                    this.progressWorksheetData.numberOfAudioSeconds = this.workItem.numberOfAudioSeconds;
                    this.progressWorksheetData.wordsToExclude = this.workItem.wordsToExclude;
                    this.progressWorksheetData.pagesToExclude = this.workItem.pagesToExclude;
                }

                    this.progressWorksheetData.action = action;
                    this.progressWorksheetData.currentErrors = [];
                    this.progressWorksheetData.dialogOpen = (!this.hasTopscribeDocuments || (action == 'complete'));
                    this.progressWorksheetData.confirmationDialogOpen = (this.hasTopscribeDocuments && (action != 'complete'));
                },
            progressWorksheet: function () {
                var data = {
                    numberOfWords: this.progressWorksheetData.numberOfWords,
                    wordsToExclude: this.progressWorksheetData.wordsToExclude,
                    pagesToExclude: this.progressWorksheetData.pagesToExclude,
                    numberOfAudioSeconds: this.progressWorksheetData.numberOfAudioSeconds,
                    numberOfPages: this.progressWorksheetData.numberOfPages,
                };
                this.progressWorksheetData.currentErrors = [];

                if (this.progressWorksheetData.action == "complete") {
                    if ((this.progressWorksheetData.ordersToBeDelivered == null) ||
                        (this.progressWorksheetData.ordersToBeDelivered.length == 0)) {
                        this.progressWorksheetData.currentErrors.push("At least one order has to be selected.");
                    }
                    data.deliveredOrders = [];
                    this.progressWorksheetData.ordersToBeDelivered.forEach((orderToBeDelivered) => {
                        if (orderToBeDelivered.deliveredOnTime) {
                            orderToBeDelivered.deliveredLateReason = null;
                            orderToBeDelivered.deliveredLateReasonText = null;
                        } else {
                            if ((orderToBeDelivered.deliveredLateReason == null) || (orderToBeDelivered.deliveredLateReason == "")) {
                                this.progressWorksheetData.currentErrors.push("A Late Reason has to be selected for each order not delivered on time.");
                            } else if (orderToBeDelivered.deliveredLateReason == 'Other') {


                                if ((orderToBeDelivered.deliveredLateReasonText == null) || (orderToBeDelivered.deliveredLateReasonText == "")) {
                                    this.progressWorksheetData.currentErrors.push("A Late Reason Text/Comment has to be specified for each order with Late Reason 'Other'.");
                                }
                            }
                        }
                        data.deliveredOrders.push(
                            {
                                orderId: orderToBeDelivered.orderId,
                                copiesDelivered: orderToBeDelivered.copiesDelivered,
                                deliveredOnTime: orderToBeDelivered.deliveredOnTime,
                                deliveredLateReason: orderToBeDelivered.deliveredLateReason,
                                deliveredLateReasonText: orderToBeDelivered.deliveredLateReasonText,
                            }
                        );
                    });

                }

                if (this.progressWorksheetData.currentErrors.length == 0) {
                    var url = this.$urlHelper.getApiUrl('workitem', 'transcript', [this.workItemId, this.progressWorksheetData.action]);
                    axios
                        .put(url, JSON.stringify(data),
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            })
                        .then(() => {
                            this.reload();
                        })
                        .catch((ex) => {
                            this.progressWorksheetData.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.progressWorksheetData.currentErrors.length == 0) {
                                this.progressWorksheetData.confirmationDialogOpen = false;
                                this.progressWorksheetData.dialogOpen = false;
                            }
                        });
                }
            },

            markUploaded: function (askForConfirmation = true) {
                if (askForConfirmation) {
                    this.$bvModal.msgBoxConfirm("Are you sure you want to mark the transcript as uploaded?",
                        {
                            title: 'Mark Uploaded',
                            size: 'md',
                            okVariant: 'danger',
                            okTitle: 'Yes',
                            cancelTitle: 'No',
                            centered: true
                        })
                        .then(value => {
                            if (value) {
                                this.markUploaded(false);
                            }
                        });
                } else {
                    var url = this.$urlHelper.getApiUrl('workitem', 'transcript', [this.workItemId, 'markuploaded']);
                    axios
                        .get(url)
                        .then(() => {
                            this.reload(false);
                        })
                        .catch(ex => {
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                }
            },
            openEditTranscriptTotalsDialog: function () {
                this.editTranscriptTotalsData.numberOfWords = this.workItem.numberOfWords;
                this.editTranscriptTotalsData.wordsToExclude = this.workItem.wordsToExclude;
                this.editTranscriptTotalsData.pagesToExclude = this.workItem.pagesToExclude;
                this.editTranscriptTotalsData.numberOfAudioSeconds = this.workItem.numberOfAudioSeconds;
                this.editTranscriptTotalsData.numberOfPages = this.workItem.numberOfPages;
                this.editTranscriptTotalsData.currentErrors = [];
                this.editTranscriptTotalsData.dialogOpen = true;
            },
            updateTranscriptTotals: function () {
                var data = {
                    numberOfWords: this.editTranscriptTotalsData.numberOfWords,
                    wordsToExclude: this.editTranscriptTotalsData.wordsToExclude,
                    pagesToExclude: this.editTranscriptTotalsData.pagesToExclude,
                    numberOfAudioSeconds: this.editTranscriptTotalsData.numberOfAudioSeconds,
                    numberOfPages: this.editTranscriptTotalsData.numberOfPages
                };
                this.editTranscriptTotalsData.currentErrors = [];
                var url = this.$urlHelper.getApiUrl('workitem', 'transcript', [this.workItemId, 'updatetotals']);
                axios
                    .put(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.editTranscriptTotalsData.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.editTranscriptTotalsData.currentErrors.length == 0) {
                            this.editTranscriptTotalsData.dialogOpen = false;
                        }
                    });
            },
            onInitialiseDueDateChange: function () {
                var oldDate = new Date(this.workItem.dueDateTimeUTC);
                var newDate = new Date(this.initializeWorksheetData.turnsDueDateTimeUTC);
                this.displayDueWarning = newDate > oldDate;
            },
            onEditDueDateChange: function () {
                var oldDate = new Date(this.workItem.dueDateTimeUTC);
                var newDate = new Date(this.currentTurnsDueDateTimeUTC);
                this.displayDueWarning = newDate > oldDate;

                this.checkPublicHoliday();
            },
            checkPublicHoliday: function () {
                //Check if manual due date is a public holiday

                if (!this.currentTurnsDueDateTimeUTC) {
                    this.displayPublicHolidayWarning = false;
                    return;
                }

                var url = this.apiBaseUrl + '/publicholiday/ispublicholiday';

                var data = {
                    date: this.formatDateTime(this.currentTurnsDueDateTimeUTC, "yyyy-MM-DD", true, false),
                    courtRegistryId: this.courtRegistryId,
                    interviewOrganisationId: this.interviewOrganisationId,
                };

                var isPublicHoliday = false;

                axios
                    .request(
                        {
                            method: 'post',
                            url: url,
                            data: JSON.stringify(data),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(response => {
                        isPublicHoliday = response.data;
                        this.displayPublicHolidayWarning = isPublicHoliday;
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    });

                this.displayPublicHolidayWarning = isPublicHoliday;
            }
            },
            mounted: function () {
                if (this.initializeWorksheetData.turnsDueDateTimeUTC != null) this.initializeWorksheetData.turnsDueDateTimeUTC = this.parseDateTime(this.initializeWorksheetData.turnsDueDateTimeUTC);
                this.reload(false); //each individual sub component will anyway load its data when mounted, no need to reload everything again when mounted
            },

            beforeDestroy: function () {
                if (this.timer != null) Visibility.stop(this.timer);
            }
        }
</script>
